<script lang="ts" setup>
import type { RouteLocationRaw } from "vue-router"
import type { IconKeys } from "@/components/ui/Icon/Icon.interface"
import type { IButtonSize, IButtonVariant } from "@/types"
import { computed, useAttrs } from "vue"
import { AppLink, Icon } from "@/components/ui"

interface ButtonLinkProps {
  variant?: IButtonVariant
  size?: IButtonSize
  href?: string | null
  to?: string | RouteLocationRaw | null
  prefixIcon?: IconKeys | null
  suffixIcon?: IconKeys | null
  disabled?: boolean
}

const props = withDefaults(defineProps<ButtonLinkProps>(), {
  variant: "primary",
  size: "default",
  disabled: false,
})

const attrs = useAttrs()

const buttonClasses = computed(() => [
  "gpx-button",
  `gpx-button_${props.variant}`,
  `gpx-button-${props.size}`,
])

const componentType = computed(() => {
  if (props.disabled)
    return "button"
  if (props.to)
    return AppLink
  if (props.href)
    return "a"
  return "button"
})

const componentProps = computed(() => ({
  ...attrs,
  ...(props.disabled && { disabled: true }),
  ...((props.to && !props.disabled) && { to: props.to }),
  ...((props.href && !props.disabled) && { href: props.href }),
}))
</script>

<template>
  <component
    :is="componentType"
    :class="buttonClasses"
    v-bind="componentProps"
  >
    <slot name="prefixIcon">
      <Icon v-if="prefixIcon" :icon="prefixIcon" :size="size" />
    </slot>
    <slot />
    <slot name="suffixIcon">
      <Icon v-if="suffixIcon" :icon="suffixIcon" :size="size" />
    </slot>
  </component>
</template>
